import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton } from '../../../components/primitives/IconButton';
import { Menu, type MenuItem } from '../../../components/primitives/Menu';
import { RecordCard } from '../../../components/primitives/RecordCard';
import { chipValueFor } from './chipUtils';
import type { BoardWorkItemRow } from './types';
import { WorkItemCardChips } from './WorkItemCardChips';

function statusSlug(status: string): string {
  return status.toLowerCase().replace(/\s+/g, '-');
}

export function BoardCard({
  card,
  visibleChipKeys,
  parentName,
  stages,
  onOpen,
  onParentOpen,
  onChangeStatus
}: {
  card: BoardWorkItemRow;
  visibleChipKeys: ReadonlyArray<string>;
  parentName: string | null;
  stages: ReadonlyArray<string>;
  onOpen: () => void;
  onParentOpen: (parentId: string) => void;
  onChangeStatus: (cardId: string, nextStatus: string) => void;
}) {
  const {
    setNodeRef,
    attributes,
    listeners,
    transform,
    transition,
    isDragging
  } = useSortable({ id: card.id });
  const parentId = card.parent_work_item_id;
  const chips = visibleChipKeys
    .map((k) => ({
      key: k,
      value: chipValueFor(k, card, parentName),
      isEpicLink: k === 'epic' && !!parentId,
      parentId
    }))
    .filter((c) => c.value != null);

  const statusItems: MenuItem[] = stages.map((stage) => {
    const isCurrent = stage === card.status;
    return {
      id: stage,
      label: isCurrent ? `✓ ${stage}` : stage,
      disabled: isCurrent,
      onSelect: () => onChangeStatus(card.id, stage),
      'data-testid': `work-items-route-board-card-${card.id}-status-${statusSlug(stage)}`
    };
  });

  return (
    <RecordCard
      ref={setNodeRef}
      layout="row"
      dragging={isDragging}
      data-testid={`work-items-route-board-card-${card.id}`}
      data-card-id={card.id}
      data-status={card.status}
      style={{
        transform: CSS.Transform.toString(transform),
        transition
      }}
      onClick={() => {
        if (isDragging) return;
        onOpen();
      }}
    >
      <RecordCard.Lead>
        <RecordCard.DragRail
          aria-label={`Reorder ${card.name}`}
          dragging={isDragging}
          data-testid={`work-items-route-board-card-${card.id}-handle`}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          {...listeners}
          {...attributes}
        />
      </RecordCard.Lead>
      <RecordCard.Body>
        <RecordCard.Title>{card.name}</RecordCard.Title>
        <WorkItemCardChips
          chips={chips}
          cardId={card.id}
          testIdPrefix="work-items-route-board-card"
          onParentOpen={onParentOpen}
        />
      </RecordCard.Body>
      <RecordCard.Actions onClick={(e) => e.stopPropagation()}>
        <Menu
          label={`Change status for ${card.name}`}
          align="end"
          strategy="fixed"
          data-testid={`work-items-route-board-card-${card.id}-status-menu`}
          trigger={(api) => (
            <IconButton
              size={24}
              aria-label={`Change status for ${card.name}`}
              data-testid={`work-items-route-board-card-${card.id}-menu`}
              {...api}
              onClick={(e) => {
                e.stopPropagation();
                api.onClick();
              }}
            >
              ⋮
            </IconButton>
          )}
          items={statusItems}
        />
      </RecordCard.Actions>
    </RecordCard>
  );
}
